import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../api/axios';

const ConfigContext = createContext();

const DEFAULT_CONFIG = {
    primaryColor: '',
    secondaryColor: '',
    backgroundColor: '',
    globalFontColor: '',
    logoUrl: '',
    buttonStyles: { borderRadius: '', padding: '' },
    pages: {},
    pageDesigns: {},
    event_letters: {},
    registration_fields: {},
    fontFamily: '',
    secondaryFontFamily: '',
    tertiaryFontFamily: '',
    backgroundImage: '',
    backgroundMobile: '',
    backgroundDesktop: '',
    elementShape: ''
};

export const ConfigProvider = ({ children }) => {
    const [config, setConfig] = useState(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const [visualRes, pagesRes, customRes, designsRes, lettersRes, regRes] = await Promise.all([
                    api.get('/configs/visual_identity').catch(() => ({ data: null })),
                    api.get('/configs/page_controls').catch(() => ({ data: null })),
                    api.get('/configs/custom_gallery_pages').catch(() => ({ data: null })),
                    api.get('/configs/page_designs').catch(() => ({ data: null })),
                    api.get('/configs/event_letters').catch(() => ({ data: null })),
                    api.get('/configs/registration_fields').catch(() => ({ data: null }))
                ]);
                
                const newConfig = {
                    ...(visualRes.data || {}),
                    pages: pagesRes.data || {},
                    customGalleryPages: customRes.data || [],
                    pageDesigns: designsRes.data || {},
                    event_letters: lettersRes.data || {},
                    registration_fields: regRes.data || {}
                };
                
                // Inject fonts to DOM
                if (newConfig.fontFamily) {
                    document.documentElement.style.setProperty('--main-font', `"${newConfig.fontFamily}", sans-serif`);
                } else {
                    document.documentElement.style.setProperty('--main-font', 'inherit');
                }
                if (newConfig.secondaryFontFamily) {
                    document.documentElement.style.setProperty('--secondary-font', `"${newConfig.secondaryFontFamily}", sans-serif`);
                } else {
                    document.documentElement.style.setProperty('--secondary-font', 'inherit');
                }
                if (newConfig.tertiaryFontFamily) {
                    document.documentElement.style.setProperty('--tertiary-font', `"${newConfig.tertiaryFontFamily}", sans-serif`);
                } else {
                    document.documentElement.style.setProperty('--tertiary-font', 'inherit');
                }

                // Inject colors to DOM
                if (newConfig.primaryColor) {
                    document.documentElement.style.setProperty('--primary-purple', newConfig.primaryColor);
                } else {
                    document.documentElement.style.setProperty('--primary-purple', '#9d4edd');
                }
                if (newConfig.backgroundColor) {
                    document.documentElement.style.setProperty('--bg-deep-purple', newConfig.backgroundColor);
                } else {
                    document.documentElement.style.setProperty('--bg-deep-purple', '#1c0042');
                }
                if (newConfig.globalFontColor) {
                    document.documentElement.style.setProperty('--global-font-color', newConfig.globalFontColor);
                } else {
                    document.documentElement.style.setProperty('--global-font-color', '#000000');
                }

                // Inject favicon dynamically
                if (newConfig.faviconUrl) {
                    let link = document.querySelector("link[rel*='icon']");
                    if (!link) {
                        link = document.createElement('link');
                        link.rel = 'shortcut icon';
                        document.getElementsByTagName('head')[0].appendChild(link);
                    }
                    link.href = newConfig.faviconUrl;
                }

                // Inject Card Border Image dynamically
                if (newConfig.cardBorderImage) {
                    document.documentElement.style.setProperty('--card-border-image', `url('${newConfig.cardBorderImage}')`);
                } else {
                    document.documentElement.style.setProperty('--card-border-image', "url('https://ftp.pcnpublicidad.com/boehringer/card_border_circuito.png')");
                }


                setConfig(prev => JSON.stringify(prev) === JSON.stringify(newConfig) ? prev : newConfig);
            } catch (error) {
                console.error("Config fetch failed, using defaults", error);
            } finally {
                setLoading(false);
            }
        };

        fetchConfig();
        const interval = setInterval(fetchConfig, 2000);
        return () => clearInterval(interval);
    }, []);

    return (
        <ConfigContext.Provider value={{ config, loading }}>
            {children}
        </ConfigContext.Provider>
    );
};

export const useConfig = () => useContext(ConfigContext);
